import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features//logging/logger';
import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import {
  DGCMessageOperator,
  DgcReceivingEvents,
  DgcSendingEvents,
  OperatorMessageEvent,
} from '@/features/game-window-integration/providers/dgc/types';
import { useDgcHandlers } from '@/features/game-window-integration/providers/dgc/use-dgc-handlers';
import { gameWindowHelperDataSelector } from '@/features/game-window-integration/store/game-window-helper-data';
import { ProviderLoggerMessages } from '@/features/game-window-integration/types';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useDgcEventHandlers = () => {
  const {
    getGameProviderGameClient,
    handleGameInitialization,
    handleGameReady,
    handleSpinStarted,
    handleSpinEnd,
    handleVolumeChange,
    handleNotImplemented,
    handleReturnToLobby,
    handleError,
  } = useDgcHandlers();

  const providerGameOrigin = useSelector(gameWindowHelperDataSelector);
  const providerEventHandlerMapper = useMemo(
    () => ({
      [DgcReceivingEvents.INITIALISE_EVENT]: handleGameInitialization,
      [DgcReceivingEvents.READY]: handleGameReady,
      [DgcReceivingEvents.CLOSE]: handleReturnToLobby,
      [DgcReceivingEvents.SOUND_ON]: handleVolumeChange,
      [DgcReceivingEvents.SOUND_OFF]: handleVolumeChange,
      [DgcReceivingEvents.SPIN_START]: handleSpinStarted,
      [DgcReceivingEvents.SPIN_END]: handleSpinEnd,
      [DgcReceivingEvents.ALERT_ACTION]: handleNotImplemented,
      [DgcReceivingEvents.WARNING]: handleError,
      [DgcReceivingEvents.FATAL_ERROR]: handleError,
    }),
    [
      handleGameInitialization,
      handleNotImplemented,
      handleGameReady,
      handleSpinEnd,
      handleVolumeChange,
      handleSpinStarted,
      handleReturnToLobby,
      handleError,
    ],
  );

  const providerEventHandler = useCallback(
    (event: OperatorMessageEvent) => {
      const { source } = event ?? {};

      if (!source) {
        return;
      }

      if (event?.data?.method) {
        if (providerEventHandlerMapper[event.data.method]) {
          providerEventHandlerMapper[event.data.method]?.(event);
        } else {
          handleUnknownEvent(event.data);
        }
      }
    },
    [providerEventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const gameProviderGameClient = getGameProviderGameClient();
      const executeEvent = (event: DGCMessageOperator) => {
        if (gameProviderGameClient && providerGameOrigin) {
          const gameFlexOrigin = providerGameOrigin;

          gameProviderGameClient?.postMessage(event, gameFlexOrigin);
        } else {
          Logger.rumError({
            name: ProviderLoggerMessages.FAILED_HANDLER_FETCH.replace(
              '%{provider}',
              Provider.DGC,
            ),
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.DGC,
            },
          });
        }
      };

      const eventResponseMapper = {
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO]: () => {
          if (e.data.payload) {
            executeEvent({
              method: DgcSendingEvents.ENABLE_AUDIO,
            });
          } else {
            executeEvent({
              method: DgcSendingEvents.DISABLE_AUDIO,
            });
          }
          WrapperProxy.setAudio(e.data.payload);
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE]: () => {
          executeEvent({
            method: DgcSendingEvents.BALANCE_RELOAD,
          });
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP]: () => {
          executeEvent({
            method: DgcSendingEvents.STOP_AUTOPLAY,
          });
        },
        [WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_RESUME]: () => {
          executeEvent({
            method: DgcSendingEvents.RESUME_AUTOPLAY,
          });
        },
      };

      eventResponseMapper[e.data.type]?.();
    },
    [providerGameOrigin, getGameProviderGameClient],
  );

  return { wrapperEventHandler, providerEventHandler };
};
